import { Autocomplete, type AutocompleteProps } from '@dev-spendesk/grapes';
import { type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type Option } from './option';
import { useCustomFieldValuesSearch } from './useCustomFieldValuesSearch';

type Props = Omit<
  AutocompleteProps<Option>,
  'options' | 'onSearch' | 'renderNoOptions' | 'onAddOption' | 'renderAddOption'
> & {
  disableSelectUndefined?: boolean;
  customField: {
    id: string;
    type: string;
    total_values?: number;
  };
} & (
    | {
        renderAddOption(rawValue: string): ReactNode; // should always return a DropdownItem component
        onAddOption(newOptionLabel: string): Option | Promise<Option>;
        renderNoOptions?(rawValue: string): ReactNode;
      }
    | {
        renderNoOptions(rawValue: string): ReactNode;
        renderAddOption?: never;
        onAddOption?: never;
      }
  );

export const CustomFieldAsyncAutocomplete = ({
  customField,
  placeholder,
  disableSelectUndefined = true,
  ...rest
}: Props) => {
  const { t } = useTranslation('global');
  const {
    mutateAsync: searchValues,
    data,
    status,
  } = useCustomFieldValuesSearch(customField.id);

  let customFieldValues: typeof data = [];
  if (status === 'success') {
    customFieldValues = data;
  }

  return (
    <Autocomplete
      options={
        disableSelectUndefined
          ? customFieldValues
          : [
              ...customFieldValues,
              { key: '', label: t('customFields.notProvided') },
            ]
      }
      placeholder={placeholder}
      onFocus={async () => {
        // Tryc catch?
        await searchValues('');
      }}
      onSearch={async (query) => {
        await searchValues(query ?? '');
      }}
      {...rest}
    />
  );
};
